// Session Blueprints — frontend types mirroring the backend serializer shapes
// (see working_mds/session_blueprints_implementation_plan.md, API contract).

/** Version lifecycle status. */
export const BLUEPRINT_DRAFT = 0;
export const BLUEPRINT_PUBLISHED = 1;

export const BLUEPRINT_STATUSES: { [key: number]: string } = {
  [BLUEPRINT_DRAFT]: 'Draft',
  [BLUEPRINT_PUBLISHED]: 'Published',
};

/** The family's stable difficulty axis entry. */
export type BlueprintDifficulty = {
  id: number;
  level: number;
  label: string;
};

/**
 * How much of what the class will trade the answer key marks, as the launch
 * card carries it. COUNTS AND TICKERS ONLY: the per-asset reasons, the matrix
 * column names and the identity hash are admin-only and live on
 * `GET /blueprints/:id/answer_key_coverage/`, because a trainer's card reaches
 * a trainee's dashboard through the same broadcast.
 */
export type LaunchCoverage = {
  /** Instruments a STARTED session can trade, including the pairs PM injects. */
  tradable: number;
  graded: number;
  not_graded: number;
  injected_fx: number;
  not_graded_tickers: string[];
  injected_fx_tickers: string[];
  complete: boolean;
};

/**
 * What a trainer needs to see BEFORE launching, served per variant by the
 * blueprint endpoint. Every field is optional and read defensively: a
 * deployment that has not shipped one yet shows the card with "not recorded"
 * in its place rather than showing nothing at all, and the socket broadcast
 * deliberately omits the answer-key gate (pm/blueprints/broadcast.py), so a
 * MISSING field means "this delivery did not carry it" — never "there is
 * none".
 */
export type LaunchSummary = {
  start_date?: string | null;
  end_date?: string | null;
  period_count?: number | null;
  /** The STARTED-session instrument count, injected FX pairs included. */
  instrument_count?: number | null;
  /** The answer key's stable code (AssessmentScenario.code). */
  answer_key?: string | null;
  answer_key_name?: string | null;
  /** 'draft' | 'active' | 'retired'. */
  answer_key_status?: string | null;
  /** The validity verdict; read through `readAnswerKeyGate`. */
  answer_key_gate?: unknown;
  /** Read through `readCoverage`. */
  coverage?: LaunchCoverage | null;
  /** false = the launch endpoint would refuse this variant. */
  launchable?: boolean | null;
  /** Participation can be recorded while its assessment is prepared later. */
  capture_only?: boolean;
  /**
   * ADMIN ONLY. Absent for a trainer or a client observer: it quotes graded
   * headlines, names matrix columns and states the pass-bar arithmetic.
   */
  launch_refusal?: string | null;
  /** Redacted: names no headline, asset, matrix column or number. Every reader gets it. */
  launch_refusal_note?: string | null;
};

/** Leaf = (version × difficulty). Owns the config carrier template session. */
export type BlueprintVariant = {
  /** Supported creation choices; normal launch permissions still apply. */
  launch_engines?: Array<'fast' | 'legacy'>;
  tandem_eligibility?: { enabled: boolean; execution_engine: string; advancement_mode: string | null };
  id: number;
  difficulty: BlueprintDifficulty;
  template_session: number; // session id (number)
  visible_to_trainers: boolean;
  /** Absent on a delivery that did not carry it (the socket broadcast). */
  launch_summary?: LaunchSummary;
};

/** A revision of the family. */
export type BlueprintVersion = {
  id: number;
  version_number: number;
  changelog: string;
  status: number; // 0 = DRAFT, 1 = PUBLISHED
  locked_at: string | null;
  is_locked: boolean;
  created: string;
  variants: BlueprintVariant[];
};

/** The blueprint family (root). */
export type Blueprint = {
  id: number;
  name: string;
  slug: string;
  description: string;
  category: string;
  created: string;
  updated: string;
  difficulties: BlueprintDifficulty[];
  versions: BlueprintVersion[];
};

// ---- Action payloads -------------------------------------------------------

export type CreateBlueprintPayload = {
  // Engine choice originates only in a new authored family.
  execution_engine?: 'legacy' | 'fast';
  fast_advancement_mode?: 'daily' | 'news';
  name: string;
  scenario_type?: number;
  category?: string;
  description?: string;
  reporting_currency?: string;
  start_date?: string;
  end_date?: string;
};

export type PromoteToBlueprintPayload = {
  session_id: number;
  name: string;
  category?: string;
  description?: string;
};

export type AddBlueprintVersionPayload = {
  id: number; // family id (path param)
  changelog?: string;
};

export type IntroduceDifficultyPayload = {
  id: number; // family id (path param)
  level: number;
  label: string;
  seed_from_difficulty_id: number;
  on_version_id: number;
};

export type PublishBlueprintVersionPayload = {
  id: number; // family id (path param)
  version_id: number;
};

export type SetVariantVisibilityPayload = {
  id: number; // family id (path param)
  variant_id: number;
  visible: boolean;
};

export type LaunchBlueprintPayload = {
  id: number; // family id (path param)
  variant_id: number;
  klass_id: number;
  execution_engine?: 'fast' | 'legacy';
};

export type LaunchBlueprintResult = {
  session: number | { id: number };
  warnings?: string[];
};

/** validate_variant response. */
export type ValidateVariantResult = {
  variant_id: number;
  template_session_id: number;
  benchmark_coverage: unknown;
  recompute_queued: boolean;
};
